import { UploadFileProps } from '@anm/components/InputFile';
import TitleBlock from '@anm/components/TitleBlock';
import Button from '@anm/components/buttons/Button/Button';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import CardModal, { CardModalProps } from '@anm/components/modals/CardModal';
import formatBytes from '@anm/helpers/size/formatBytes';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, useRef, useState, FC } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';

import { ButtonsContainer, CropArea, Wrapper } from './Wrapper';
import { centerSelection, cropImage, logger } from './helpers';

const defaultCrop = { unit: '%', width: 100, aspect: 16 / 9 } as Crop;

export type PreviewSelectProps = UploadFileProps & { isEditDestination: boolean };

type CropModalProps = CardModalProps & {
  src: string;
  file: File | null;
  isEditDestination: boolean;
  onCreateUpload: (p: PreviewSelectProps) => void;
};

const CropModal: FC<CropModalProps> = ({ src, file, isEditDestination, onCreateUpload, onModalClose }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>(defaultCrop);
  const [isPending, setPending] = useSwitchState();

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result as string));

      reader.readAsDataURL(file);

      logger.info('Original file size ', formatBytes({ bytes: file.size }));
    }
  }, [file]);

  const onLoad = useCallback((img: HTMLImageElement) => {
    imgRef.current = img;

    centerSelection(img, setCrop);

    return false;
  }, []);

  const handleCrop = useCallback(() => {
    setPending();
    cropImage(crop, imgRef.current, isEditDestination, onCreateUpload);
  }, [crop, imgRef.current, setPending, onCreateUpload]);

  return (
    <CardModal {...{ onModalClose }}>
      <Wrapper>
        <TitleBlock title="Crop your thumbnail" size="super-medium" />
        <CropArea>
          <ReactCrop crop={crop} src={imgSrc} onImageLoaded={onLoad} onChange={setCrop} onComplete={setCrop} />
        </CropArea>
        <ButtonsContainer>
          <Button variant="silver-border" size="large" onClick={onModalClose}>
            Back
          </Button>
          <ButtonLoader size="large" variant="light-blue" onClick={handleCrop} {...{ isPending }}>
            Crop
          </ButtonLoader>
        </ButtonsContainer>
      </Wrapper>
    </CardModal>
  );
};

export default CropModal;
